import { Link } from "react-router-dom";
import { siteMeta, principles } from "@/mocks/chigua";
import useSeo from "@/hooks/useSeo";
import Breadcrumbs from "@/components/chigua/Breadcrumbs";
import JsonLd from "@/components/chigua/JsonLd";

const testimonials = [
  {
    who: "一位追综多年的读者",
    tag: "深扒档案常客",
    text: "别的平台是把热搜甩给我，今日吃瓜是把一条热搜的来龙去脉摊成时间线给我。看完我常常能自己判断谁在带节奏。",
  },
  {
    who: "关注行业内容的自媒体人",
    tag: "栏目合作",
    text: "难得见到这么克制的写法，观点让给材料自己说话。做内容的人能理解，克制比热闹难多了。",
  },
  {
    who: "路转粉的普通观众",
    tag: "光顾一个月的读者",
    text: "一开始只是想看热闹，后来发现这里的“内幕消息”和“内幕真相”会清楚标着信源，反倒让我学会分辨真假了。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们-今日吃瓜的品牌定位与发展理念",
    description:
      "了解今日吃瓜：我们是一支只按公开材料整理内幕消息与内幕真相、用时间线复盘娱乐行业内幕的内容团队。看品牌定位、发展理念、内容原则与用户口碑。",
    path: "/about",
    keywords: "今日吃瓜,今日吃瓜内幕深扒,今日吃瓜关于我们,内容原则,时间线复盘",
  });

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteMeta.brand,
      url: siteMeta.domain,
      email: siteMeta.email,
      description: siteMeta.description,
      slogan: siteMeta.slogan,
    },
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们-今日吃瓜",
      url: `${siteMeta.domain}/about`,
      description: "今日吃瓜的品牌定位、发展理念、内容原则与用户口碑。",
      inLanguage: "zh-CN",
      about: { "@type": "Organization", name: siteMeta.brand },
    },
  ];

  return (
    <div className="mx-auto max-w-[1180px] px-4 md:px-6">
      <JsonLd data={schema} />
      <Breadcrumbs items={[{ label: "关于我们" }]} />

      <header className="mx-auto max-w-3xl text-center">
        <span className="inline-flex items-center gap-1.5 rounded bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
          <i className="ri-information-line" aria-hidden="true" />
          品牌简介
        </span>
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于今日吃瓜</h1>
        <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-7 text-foreground-600">
          我们相信，判断力源于完整的信息。今日吃瓜存在的意义，就是把散落在各处的
          <strong className="font-semibold text-foreground-900">内幕消息</strong>整理成看得懂的时间线，让每一件
          <strong className="font-semibold text-foreground-900">内幕真相</strong>都有自己的来龙去脉——一网看尽，不用翻。
        </p>
      </header>

      {/* 品牌定位 */}
      <section className="mt-12 grid items-center gap-8 lg:grid-cols-2">
        <div className="rounded-xl border border-background-200 bg-background-100/50 p-7 md:p-9">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">我们是一支怎样的内容团队</h2>
          <p className="mt-4 text-[15px] leading-8 text-foreground-700">
            今日吃瓜由一群关注影视、音乐、综艺与粉丝生态的内容编辑组成。我们不追着一手流量跑，而是反着来：等事件沉淀、等材料到齐、等时间线清晰，再把完整的叙事交给你。
          </p>
          <p className="mt-4 text-[15px] leading-8 text-foreground-700">
            站内每篇复盘都建立在一个朴素的前提上——<strong className="text-primary-700">只整理公开材料</strong>。官方公告、播出画面、可检索报道，是我们的三大信源底座。无法核验的匿名消息，我们宁可标注存疑，也不让它冒充“真相”混进正文。
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            { icon: "ri-eye-line", t: "看得见的方法", d: "给信源分级，标出每条线索的分量，让读者知道证据到哪一步。" },
            { icon: "ri-arrow-up-down-line", t: "守得住的分寸", d: "整理不等于定性，未定论的事件如实标注，把判断权交还读者。" },
            { icon: "ri-group-line", t: "耐得了的耐心", d: "愿意等事件沉淀，用时间换完整度，不为一时的流量仓促下笔。" },
            { icon: "ri-shield-check-line", t: "担得起的责任", d: "公开可溯源是底线，指正与纠错同样被我们视为内容的一部分。" },
          ].map((v) => (
            <div key={v.t} className="rounded-xl border border-background-200 bg-background-50 p-5">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary-100 text-xl text-primary-700">
                <i className={v.icon} aria-hidden="true" />
              </span>
              <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{v.t}</h3>
              <p className="mt-1.5 text-sm leading-6 text-foreground-600">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 内容原则（简版） */}
      <section className="mt-12 rounded-xl border border-secondary-200 bg-secondary-100/40 p-7 md:p-9">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="max-w-xl">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">我们甄别“内幕”的四条原则</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              内容原则不是挂在墙上的口号，而是每一篇复盘都会回望的检查清单。这也是我们敢于把“内幕消息”与“内幕真相”放在一起谈论的底气。
            </p>
          </div>
          <Link
            to="/#faq"
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-secondary-600 px-5 py-3 text-sm font-semibold text-background-50 transition hover:bg-secondary-700"
          >
            常见问题速览
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((p) => (
            <div key={p.n} className="rounded-lg bg-background-50 p-4">
              <p className="font-display-sc text-2xl text-primary-500">{p.n}</p>
              <p className="mt-1 font-heading font-bold text-foreground-950">{p.title}</p>
              <p className="mt-1 text-xs leading-5 text-foreground-600">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="mt-12">
        <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
        <p className="mt-2 text-center text-sm text-foreground-500">来自不同阅读习惯的读者的真实反馈</p>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.who} className="rounded-xl border border-background-200 bg-background-50 p-6">
              <i className="ri-double-quotes-l text-3xl text-primary-300" aria-hidden="true" />
              <blockquote className="mt-2 text-sm leading-7 text-foreground-700">“{t.text}”</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-primary-100 font-label-sc text-base text-primary-700">
                  {t.who.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-900">{t.who}</span>
                  <span className="text-xs text-foreground-500">{t.tag}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 联系 CTA */}
      <section className="mt-12 mb-16 overflow-hidden rounded-xl bg-foreground-950 px-7 py-10 text-center text-background-100 md:px-10">
        <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">和我们一起，把时间线拼完整</h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-background-300">
          无论是指正一篇复盘，还是提供一条可核验的公开线索，你的参与都在让「今日吃瓜」的内幕整理更接近完整。
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-[15px] font-semibold text-background-50 transition hover:bg-primary-600"
          >
            提交线索
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
          <a
            href={`mailto:${siteMeta.email}`}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200/20 px-6 py-3 text-[15px] font-medium text-background-100 transition hover:bg-background-50 hover:text-foreground-900"
            rel="nofollow"
          >
            {siteMeta.email}
          </a>
        </div>
      </section>
    </div>
  );
}